/**
 * TanStack Query hooks for API data fetching.
 */

import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, insightsApi, postsApi, heatmapApi, accountsApi } from "./api";

export function useUser() {
  return useQuery({
    queryKey: ["user"],
    queryFn: authApi.me,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useInsightsSummary(accountId: string | null, period: string) {
  return useQuery({
    queryKey: ["insights", "summary", accountId, period],
    queryFn: () => insightsApi.summary(accountId!, period),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInsightsDaily(
  accountId: string | null,
  period: string,
  metric: string
) {
  return useQuery({
    queryKey: ["insights", "daily", accountId, period, metric],
    queryFn: () => insightsApi.daily(accountId!, period, metric),
    enabled: !!accountId && !!metric,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePosts(
  accountId: string | null,
  period: string,
  sort: string,
  page: number
) {
  return useQuery({
    queryKey: ["posts", accountId, period, sort, page],
    queryFn: () => postsApi.list(accountId!, period, sort, page),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useHeatmap(accountId: string | null, period: string) {
  return useQuery({
    queryKey: ["heatmap", accountId, period],
    queryFn: () => heatmapApi.data(accountId!, period),
    enabled: !!accountId,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Mutation hook to trigger a manual sync for Pro users.
 * After enqueue, schedules delayed query invalidations to pick up new data.
 */
export function useSyncTrigger() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => accountsApi.sync(accountId),
    onSuccess: (_data, accountId) => {
      // Poll for sync completion: refetch at 5s, 15s, 30s after enqueue
      const delays = [5000, 15000, 30000];
      delays.forEach((d) => {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["user"] });
          queryClient.invalidateQueries({ queryKey: ["insights", "summary", accountId] });
          queryClient.invalidateQueries({ queryKey: ["insights", "daily", accountId] });
          queryClient.invalidateQueries({ queryKey: ["posts", accountId] });
          queryClient.invalidateQueries({ queryKey: ["heatmap", accountId] });
        }, d);
      });
    },
  });
}

/**
 * Polls for sync completion when an account has never been synced.
 * Returns whether the active account is currently awaiting its first sync.
 *
 * When sync completes (lastSyncedAt transitions from null to non-null),
 * invalidates all data queries so the UI refetches with fresh data.
 */
export function useSyncStatus(
  accountId: string | null,
  lastSyncedAt: number | null
) {
  const queryClient = useQueryClient();
  const isAwaitingFirstSync = lastSyncedAt === null && !!accountId;
  const prevAwaitingRef = useRef(isAwaitingFirstSync);

  // When isAwaitingFirstSync transitions from true → false, sync just completed.
  // Invalidate all data queries to refetch with fresh data.
  useEffect(() => {
    if (prevAwaitingRef.current && !isAwaitingFirstSync && accountId) {
      queryClient.invalidateQueries({ queryKey: ["insights", "summary", accountId] });
      queryClient.invalidateQueries({ queryKey: ["insights", "daily", accountId] });
      queryClient.invalidateQueries({ queryKey: ["posts", accountId] });
      queryClient.invalidateQueries({ queryKey: ["heatmap", accountId] });
    }
    prevAwaitingRef.current = isAwaitingFirstSync;
  }, [isAwaitingFirstSync, accountId, queryClient]);

  useQuery({
    queryKey: ["sync-poll", accountId],
    queryFn: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user"] });
      return { polledAt: Date.now() };
    },
    enabled: isAwaitingFirstSync,
    refetchInterval: 10_000, // 10 seconds
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  return { isAwaitingFirstSync };
}
